import { User, MoreVertical, Edit2, Archive, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import { DAY_NAMES } from '../hooks/useOneOnOneRelationships';

export default function RelationshipCard({
  relationship,
  isSelected,
  onClick,
  onEdit,
  onArchive,
  onRestore,
  topicCount = 0,
  actionItemCount = 0
}) {
  const [showMenu, setShowMenu] = useState(false);

  const isArchived = relationship.status === 'archived';
  const dayName = DAY_NAMES[relationship.meetingDay] || 'Unknown';

  const handleMenuClick = (e) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    onEdit?.(relationship);
  };

  const handleArchive = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    onArchive?.(relationship);
  };

  const handleRestore = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    onRestore?.(relationship);
  };

  return (
    <div
      onClick={onClick}
      className={`
        relative p-3 rounded-lg cursor-pointer transition-all
        ${isSelected
          ? 'bg-accent-muted border-2 border-accent shadow-lg shadow-accent/10'
          : 'bg-surface-card border border-border hover:border-border-strong hover:bg-surface-hover'
        }
        ${isArchived ? 'opacity-60' : ''}
      `}
    >
      {/* Header with name and menu */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className={`
            p-1.5 rounded-lg
            ${isSelected ? 'bg-accent/20' : 'bg-surface-hover'}
          `}>
            <User size={16} className={isSelected ? 'text-accent' : 'text-text-secondary'} />
          </div>
          <div>
            <h3 className={`text-sm font-semibold ${isSelected ? 'text-accent' : 'text-text-primary'}`}>
              {relationship.personName}
            </h3>
            <p className="text-[10px] text-text-tertiary">
              {dayName}s
            </p>
          </div>
        </div>

        {/* Menu button */}
        <div className="relative">
          <button
            onClick={handleMenuClick}
            className="p-1 rounded-md bg-transparent border-none text-text-tertiary cursor-pointer hover:bg-surface-active hover:text-text-primary transition-all"
          >
            <MoreVertical size={14} />
          </button>

          {showMenu && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 z-10"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMenu(false);
                }}
              />
              {/* Menu */}
              <div className="absolute right-0 top-full mt-1 bg-surface-card border border-border rounded-lg shadow-lg z-20 min-w-[120px] overflow-hidden">
                <button
                  onClick={handleEdit}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-text-secondary hover:bg-surface-hover cursor-pointer border-none bg-transparent"
                >
                  <Edit2 size={12} />
                  Edit
                </button>
                {isArchived ? (
                  <button
                    onClick={handleRestore}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-text-secondary hover:bg-surface-hover cursor-pointer border-none bg-transparent"
                  >
                    <RotateCcw size={12} />
                    Restore
                  </button>
                ) : (
                  <button
                    onClick={handleArchive}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-400 hover:bg-red-500/10 cursor-pointer border-none bg-transparent"
                  >
                    <Archive size={12} />
                    Archive
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Stats row */}
      {!isArchived && (topicCount > 0 || actionItemCount > 0) && (
        <div className="flex items-center gap-3 mt-2 pt-2 border-t border-border">
          {topicCount > 0 && (
            <span className="text-[10px] text-text-tertiary">
              {topicCount} topic{topicCount !== 1 ? 's' : ''}
            </span>
          )}
          {actionItemCount > 0 && (
            <span className="text-[10px] text-text-tertiary">
              {actionItemCount} action{actionItemCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      )}

      {/* Archived badge */}
      {isArchived && (
        <div className="mt-2 pt-2 border-t border-border">
          <span className="text-[10px] text-text-tertiary italic">Archived</span>
        </div>
      )}
    </div>
  );
}
